import { useCallback, createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import type { User } from '../api/types';

interface AuthValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<User>('/auth/me');
      } catch (err) {
        // 401 non e' un errore da mostrare: e' semplicemente "non loggato".
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const loginMutation = useMutation({
    mutationFn: (body: { email: string; password: string }) => api.post<User>('/auth/login', body),
    onSuccess: (user) => queryClient.setQueryData(['me'], user),
  });

  const registerMutation = useMutation({
    mutationFn: (body: { email: string; password: string; name?: string }) =>
      api.post<User>('/auth/register', body),
    onSuccess: (user) => queryClient.setQueryData(['me'], user),
  });

  const loginWithGoogle = useCallback(
    async (credential: string) => {
      const user = await api.post<User>('/auth/google', { credential });
      queryClient.setQueryData(['me'], user);
    },
    [queryClient],
  );

  const value = useMemo<AuthValue>(
    () => ({
      user: data ?? null,
      loading: isLoading,
      login: async (email, password) => {
        await loginMutation.mutateAsync({ email, password });
      },
      register: async (email, password, name) => {
        await registerMutation.mutateAsync({ email, password, name });
      },
      loginWithGoogle,
      logout: async () => {
        await api.post('/auth/logout');
        // Si rimuove tutto tranne 'me': clear() cancellava anche la query
        // 'me' appena scritta, lasciando il Gate senza modo di accorgersi
        // del logout finche' non si ricaricava la pagina a mano.
        queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
        queryClient.setQueryData(['me'], null);
      },
    }),
    [data, isLoading, loginMutation, registerMutation, queryClient, loginWithGoogle],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuori da AuthProvider');
  return ctx;
}
